/**
 * Editorial Authoring V2 — spread workspace (BUILD 3: Story branch + handoff).
 *
 * Opened from a gallery card, as an in-page overlay (no route change).
 * Deliberately does NOT import MasterEditor, MasterEditorLauncher,
 * DetailedEditorShell, ImageSlot, the old CropModal, MediaLibraryPicker,
 * or anything else from @/components/editorial/ (the legacy authoring
 * UI). The "Из Медиатеки" picker and the two media endpoint paths now
 * live in ./EditorialMediaPickerV2 (BUILD 2 extraction) so this
 * generic body, EditorialHeroV2.jsx and EditorialStoryV2.jsx can all
 * reuse them without an import cycle.
 *
 * for block.master_type === "HERO" → EditorialHeroV2.
 * for block.master_type === "EDITORIAL_STORY" → EditorialStoryV2.
 * for block.master_type === "VISUAL_STORY" → EditorialVisualStoryV2.
 * for block.master_type === "MACRO" → EditorialMacroV2.
 * for block.master_type === "MACRO_SENSORY" → EditorialMacroSensoryV2.
 * for block.master_type === "PORTRAIT_DIALOGUE" → EditorialPortraitDialogueV2.
 * for block.master_type === "PROCESS_STORY" → EditorialProcessStoryV2.
 * every other MASTER → the unchanged generic body below (paste text,
 * attach a photo, disabled "Распознать текст").
 *
 * BUILD 3: onContinueInStory and handoffSource are new pass-through
 * props (both optional; unused by the generic body). Routing happens
 * in the hook-free wrapper below; the generic body is a separate
 * component so React Hooks are never called conditionally.
 *
 * SAVE CONTRACT (generic body, unchanged) — read before changing what
 * gets persisted: to satisfy "no new backend fields", this screen
 * writes into the two generic content fields that already exist in
 * the current schema (visible in the legacy BlockEditor fallback in
 * Editorial.jsx): content.body and content.media_ids. It merges into
 * the block's existing content rather than replacing it, so
 * master-specific fields already set by other tooling (e.g. HERO's
 * hero_url, hero_media_id, extra.*) are never touched or normalized
 * away.
 *
 * MEDIA CONTRACT — two endpoints (now in ./EditorialMediaPickerV2):
 *   GET  /admin/media
 *   POST /admin/media/upload-file
 * Both are called through the existing `api` client without an "/api"
 * prefix, matching the actual frontend api client contract.
 */
import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { mediaRecordUrl } from "@/lib/editorialMedia.mjs";
import { scheduleEditorialDirtyAfterInput } from "@/lib/editorialInputCommitV1.mjs";
import { masterLabelV2 } from "./EditorialMaterialGalleryV2";
import { resolveSpreadThumbnailV2 } from "./editorialSpreadDisplayV2.mjs";
import { MediaLibraryPickerV2, MEDIA_LIST, MEDIA_UPLOAD } from "./EditorialMediaPickerV2";
import { EditorialHeroV2 } from "./EditorialHeroV2";
import { EditorialStoryV2 } from "./EditorialStoryV2";
import { EditorialVisualStoryV2 } from "./EditorialVisualStoryV2";
import { EditorialMacroV2 } from "./EditorialMacroV2";
import { EditorialMacroSensoryV2 } from "./EditorialMacroSensoryV2";
import { EditorialPortraitDialogueV2 } from "./EditorialPortraitDialogueV2";
import { EditorialProcessStoryV2 } from "./EditorialProcessStoryV2";
import { EditorialRecipeV2 } from "./EditorialRecipeV2";
import { EditorialHistoricalV2 } from "./EditorialHistoricalV2";
import { EditorialKnowledgeV2 } from "./EditorialKnowledgeV2";
import { EditorialVideoV2 } from "./EditorialVideoV2";
import { EditorialSpreadPublicationControlsV1 } from "./EditorialSpreadPublicationControlsV1";

function destinationLines(volume) {
  if (!volume) {
    return { title: "Место публикации будет указано здесь", access: null };
  }
  return {
    title: volume.title,
    access: volume.product_id ? "Доступ: по связанному продукту" : "Доступ: не указан",
  };
}

export function EditorialSpreadWorkspaceV2({ block, volume, onClose, onSaved, onContinueInStory, handoffSource, sourceMaterial, newStoryMode = false, onStartNewStory, onAuthorCreated }) {
  const [publicationDirty, setPublicationDirty] = useState(false);
  // Input handlers in MASTER editors are controlled. Their shared capture
  // handler must not re-render the parent before the field's own onChange has
  // accepted the keystroke (React 19 can otherwise lose the first character).
  // Epoch invalidates pending callbacks after save, block change or unmount.
  const inputEpochRef = useRef(0);
  useEffect(() => {
    inputEpochRef.current += 1;
    setPublicationDirty(false);
    return () => { inputEpochRef.current += 1; };
  }, [block.id]);
  const markDirty = (event) => {
    // The publication date/mode controls are not edits to the saved MASTER.
    if (event.target?.closest?.('[data-spread-publication-controls="true"]')) return;
    const epoch = inputEpochRef.current;
    scheduleEditorialDirtyAfterInput({
      isCurrent: () => inputEpochRef.current === epoch,
      onDirty: () => setPublicationDirty(true),
    });
  };
  const savedAndReset = (...args) => {
    inputEpochRef.current += 1;
    setPublicationDirty(false);
    onSaved?.(...args);
  };
  if (block.master_type === "HERO") {
    return (
      <EditorialHeroV2 block={block} volume={volume} onClose={onClose} onSaved={savedAndReset} publicationDirty={publicationDirty} onPublicationInput={markDirty}
        onContinueInStory={onContinueInStory} />
    );
  }
  if (block.master_type === "EDITORIAL_STORY") {
    return (
      <EditorialStoryV2 block={block} volume={volume} onClose={onClose} onSaved={savedAndReset} publicationDirty={publicationDirty} onPublicationInput={markDirty}
        handoffSource={newStoryMode ? null : handoffSource} sourceMaterial={sourceMaterial} newStoryMode={newStoryMode}
        onStartNewStory={onStartNewStory} onAuthorCreated={onAuthorCreated} />
    );
  }
  if (block.master_type === "VISUAL_STORY") {
    return (
      <EditorialVisualStoryV2
        block={block}
        volume={volume}
        onClose={onClose}
        onSaved={savedAndReset} publicationDirty={publicationDirty} onPublicationInput={markDirty}
      />
    );
  }

  if (block.master_type === "MACRO") {
    return (
      <EditorialMacroV2
        block={block}
        volume={volume}
        onClose={onClose}
        onSaved={savedAndReset} publicationDirty={publicationDirty} onPublicationInput={markDirty}
      />
    );
  }

  if (block.master_type === "MACRO_SENSORY") {
    return (
      <EditorialMacroSensoryV2
        block={block}
        volume={volume}
        onClose={onClose}
        onSaved={savedAndReset} publicationDirty={publicationDirty} onPublicationInput={markDirty}
      />
    );
  }

  if (block.master_type === "PORTRAIT_DIALOGUE") {
    return (
      <EditorialPortraitDialogueV2
        block={block}
        volume={volume}
        onClose={onClose}
        onSaved={savedAndReset} publicationDirty={publicationDirty} onPublicationInput={markDirty}
      />
    );
  }

  if (block.master_type === "PROCESS_STORY") {
    return (
      <EditorialProcessStoryV2
        block={block}
        volume={volume}
        onClose={onClose}
        onSaved={savedAndReset} publicationDirty={publicationDirty} onPublicationInput={markDirty}
      />
    );
  }

  if (block.master_type === "RECIPE") {
    return (
      <EditorialRecipeV2
        block={block}
        volume={volume}
        onClose={onClose}
        onSaved={savedAndReset} publicationDirty={publicationDirty} onPublicationInput={markDirty}
      />
    );
  }

  if (block.master_type === "HISTORICAL_EDITORIAL") {
    return (
      <EditorialHistoricalV2
        block={block}
        volume={volume}
        onClose={onClose}
        onSaved={savedAndReset} publicationDirty={publicationDirty} onPublicationInput={markDirty}
      />
    );
  }

  if (block.master_type === "KNOWLEDGE") {
    return (
      <EditorialKnowledgeV2
        block={block}
        volume={volume}
        onClose={onClose}
        onSaved={savedAndReset} publicationDirty={publicationDirty} onPublicationInput={markDirty}
      />
    );
  }

  if (block.master_type === "VIDEO") {
    return (
      <EditorialVideoV2
        block={block}
        volume={volume}
        onClose={onClose}
        onSaved={savedAndReset} publicationDirty={publicationDirty} onPublicationInput={markDirty}
      />
    );
  }

  return (
    <GenericEditorialSpreadWorkspaceV2
      block={block}
      volume={volume}
      onClose={onClose}
      onSaved={savedAndReset} publicationDirty={publicationDirty} onPublicationInput={markDirty}
    />
  );
}

function GenericEditorialSpreadWorkspaceV2({ block, volume, onClose, onSaved, publicationDirty, onPublicationInput }) {
  const c = block.content || {};
  const [text, setText] = useState(c.body || "");
  const [mediaItems, setMediaItems] = useState(
    (Array.isArray(c.media_ids) ? c.media_ids : []).map((id) => ({ id, url: null }))
  );
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [saveState, setSaveState] = useState("idle");
  const fileInputRef = useRef(null);

  useEffect(() => {
    setText(c.body || "");
    setMediaItems((Array.isArray(c.media_ids) ? c.media_ids : []).map((id) => ({ id, url: null })));
    setSaveState("idle");
  }, [block.id]);

  useEffect(() => {
    const ids = Array.isArray(c.media_ids) ? c.media_ids : [];
    if (!ids.length) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(MEDIA_LIST);
        if (cancelled) return;
        const records = Array.isArray(data) ? data : (data?.items || data?.results || []);
        const byId = new Map(records.map((r) => [r.id, r]));
        setMediaItems((items) => items.map((m) => {
          const record = byId.get(m.id);
          return record ? { id: m.id, url: mediaRecordUrl(record) } : m;
        }));
      } catch {
        // Non-fatal: unresolved items keep the neutral placeholder.
      }
    })();
    return () => { cancelled = true; };
  }, [block.id]);

  const title = c.headline || c.title || c.subtitle || masterLabelV2(block.master_type);
  const destination = destinationLines(volume);
  const previewImage = mediaItems.find((m) => m.url)?.url || resolveSpreadThumbnailV2(c);

  const pickFromLibrary = (media) => {
    const url = mediaRecordUrl(media) || media.file_url || media.url || null;
    setMediaItems((items) => [...items.filter((m) => m.id !== media.id), { id: media.id, url }]);
    setLibraryOpen(false);
  };

  const removeMedia = (id) => {
    setMediaItems((items) => items.filter((m) => m.id !== id));
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(MEDIA_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = mediaRecordUrl(data) || data.file_url || data.url || null;
      setMediaItems((items) => [...items.filter((m) => m.id !== data.id), { id: data.id, url }]);
      toast.success("Фото загружено");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaveState("saving");
    try {
      const nextContent = {
        ...c,
        body: text,
        media_ids: mediaItems.map((m) => m.id),
      };
      await api.patch(`/admin/editorial/blocks/${block.id}`, { content: nextContent });
      setSaveState("saved");
      onSaved?.({ ...block, content: nextContent }, mediaItems.filter((m) => m.url));
      toast.success("Изменения сохранены");
      setTimeout(() => setSaveState("idle"), 1500);
    } catch (e) {
      setSaveState("error");
      toast.error(formatApiError(e));
    }
  };

  const saveLabel = {
    idle: "", saving: "Сохранение…", saved: "Сохранено", error: "Ошибка сохранения",
  }[saveState];

  return (
    <div
      onInputCapture={onPublicationInput} onChangeCapture={onPublicationInput}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="editorial-v2-workspace"
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-cream">
        <div className="flex items-start justify-between gap-3 border-b border-espresso/10 px-5 py-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">
              {masterLabelV2(block.master_type)}
            </div>
            <div className="mt-0.5 truncate font-serif text-xl text-espresso">{title}</div>
          </div>
          {saveLabel && (
            <span className="shrink-0 pt-1 text-[11px] uppercase tracking-widest text-espresso/50"
              data-testid="editorial-v2-save-state">
              {saveLabel}
            </span>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          <div className="rounded-2xl border border-espresso/10 bg-white/70 px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-espresso/55">Где будет опубликовано</div>
            <div className="mt-1 text-sm text-espresso/75" data-testid="editorial-v2-destination">
              {destination.title}
            </div>
            {destination.access && (
              <div className="mt-0.5 text-xs text-espresso/55" data-testid="editorial-v2-destination-access">
                {destination.access}
              </div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-[11px] text-espresso/65">Вставить текст</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              placeholder="Вставьте текст этого разворота — Редакция сама подготовит структуру."
              className="w-full rounded-xl border border-espresso/20 bg-white px-3 py-3 text-sm leading-relaxed outline-none focus:border-espresso/45"
              data-testid="editorial-v2-text"
            />
          </div>

          <div>
            <div className="mb-1 flex items-center gap-2">
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso disabled:opacity-50"
                data-testid="editorial-v2-add-photo">
                {uploading ? "Загрузка…" : "Добавить фотографию"}
              </button>
              <button type="button" onClick={() => setLibraryOpen(true)}
                className="rounded-lg border border-espresso/20 bg-white px-3 py-2 text-xs text-espresso"
                data-testid="editorial-v2-open-library">
                Из Медиатеки
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelected}
                data-testid="editorial-v2-file-input"
              />
            </div>
            {mediaItems.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {mediaItems.map((m) => (
                  <div key={m.id}
                    className="relative h-14 w-14 overflow-hidden rounded-lg border border-espresso/15 bg-white">
                    {m.url ? (
                      <img src={m.url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[9px] text-espresso/50">
                        Фото
                      </div>
                    )}
                    <button type="button" onClick={() => removeMedia(m.id)}
                      className="absolute right-0.5 top-0.5 rounded-full bg-black/50 px-1 text-[10px] text-white"
                      aria-label="Убрать фото">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <button type="button" disabled aria-disabled="true"
              className="rounded-xl border border-espresso/15 bg-cream/60 px-5 py-2.5 text-sm text-espresso/45"
              data-testid="editorial-v2-recognize-disabled">
              Распознавание — следующий шаг
            </button>
            <p className="mt-2 text-xs text-espresso/60">
              Сейчас доступно ручное сохранение текста и фото.
            </p>
          </div>

          {showPreview && (
            <div className="rounded-2xl border border-espresso/15 bg-white px-4 py-4" data-testid="editorial-v2-local-preview">
              <div className="mb-3 text-[10px] uppercase tracking-widest text-espresso/50">
                Предпросмотр разворота
              </div>
              {previewImage && (
                <div className="mb-4 overflow-hidden rounded-xl bg-cream/70">
                  <img src={previewImage} alt="" className="max-h-64 w-full object-cover" />
                </div>
              )}
              <div className="font-serif text-2xl leading-tight text-espresso">{title}</div>
              {text && <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-espresso/75">{text}</div>}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-espresso/10 px-5 py-4">
          <EditorialSpreadPublicationControlsV1 block={block} dirty={publicationDirty} />
          <button type="button" onClick={onClose}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
            data-testid="editorial-v2-close">
            Закрыть
          </button>
          <button type="button" onClick={() => setShowPreview((v) => !v)}
            className="rounded-xl border border-espresso/20 bg-white px-4 py-2 text-sm text-espresso"
            data-testid="editorial-v2-preview-toggle">
            {showPreview ? "Скрыть предпросмотр" : "Предпросмотр разворота"}
          </button>
          <button type="button" onClick={save} disabled={saveState === "saving"}
            className="rounded-xl bg-espresso px-4 py-2 text-sm text-cream disabled:opacity-50"
            data-testid="editorial-v2-save">
            Сохранить изменения
          </button>
        </div>
      </div>

      <MediaLibraryPickerV2 open={libraryOpen} onClose={() => setLibraryOpen(false)} onPick={pickFromLibrary} />
    </div>
  );
}
